import { useState } from 'react'
import { EventConfig, KioskSettings as KioskSettingsType } from '@/types'
import { Switch } from '@/admin/components/Switch'
import { Monitor, RefreshCw, Layout, Clock, Type, QrCode, Sparkles, Zap, Palette } from 'lucide-react'
import { useI18n } from '@/lib/i18n'
import { generateShareToken, calculateExpiration } from '@/lib/utils'

interface KioskSettingsProps {
  event: EventConfig
  onChange: (updates: Partial<EventConfig>) => void
}

export default function KioskSettings({ event, onChange }: KioskSettingsProps) {
  const { t } = useI18n()
  const [copied, setCopied] = useState(false)

  const kiosk: KioskSettingsType = event.kiosk || {
    enabled: true,
    publicAccess: 'disabled',
    publicAccessDuration: 'permanent',
    publicAccessDurationValue: 1,
    publicAccessExpiresAt: null,
    publicAccessCreatedAt: null,
    sortOrder: 'newestFirst',
    contentFilter: 'all',
    interval: 10,
    showEventName: true,
    showQrCode: true,
    autoRefreshInterval: 10,
    refreshOnNewPhoto: true,
    blurBackground: true,
    transitionEffect: 'fade',
    loopSlideshow: true,
    backgroundColor: '#000000',
  }

  const update = (updates: Partial<KioskSettingsType>) => {
    onChange({ kiosk: { ...kiosk, ...updates } })
  }

  return (
    <div className="max-w-3xl space-y-8 pb-12">
      <div>
        <h2 className="text-xl font-bold">{t('kiosk.title')}</h2>
        <p className="text-sm text-muted-foreground mt-1">{t('kiosk.description')}</p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        {/* Kiosk Slideshow Status */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="rounded-lg bg-primary/10 p-2 text-primary">
                <Monitor className="h-5 w-5" />
              </div>
              <span className="font-semibold">{t('kiosk.status')}</span>
            </div>
            <Switch
              checked={kiosk.enabled}
              onCheckedChange={(enabled: boolean) => update({ enabled })}
            />
          </div>
          <p className="text-xs text-muted-foreground">{t('kiosk.statusDesc')}</p>
        </div>

        {/* Public Access Visibility */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:col-span-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="rounded-lg bg-emerald-500/10 p-2 text-emerald-400">
                <Monitor className="h-5 w-5" />
              </div>
              <div className="flex flex-col">
                <span className="font-semibold">Public Access Visibility</span>
                <span className="text-xs text-muted-foreground mt-0.5">Allow viewing the kiosk without signing in.</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold text-muted-foreground">
                {kiosk.publicAccess === 'enabled' ? 'Public (Everyone)' : 'Private (Signed-in only)'}
              </span>
              <Switch
                checked={kiosk.publicAccess === 'enabled'}
                onCheckedChange={(enabled: boolean) => {
                  const publicAccess = enabled ? 'enabled' : 'disabled';
                  let expiresAt = null;
                  let publicToken = kiosk.publicToken;
                  if (enabled) {
                    expiresAt = calculateExpiration(kiosk.publicAccessDuration || 'permanent', kiosk.publicAccessDurationValue || 1);
                    if (!publicToken) {
                      publicToken = generateShareToken();
                    }
                  }
                  update({ 
                    publicAccess, 
                    publicAccessExpiresAt: expiresAt,
                    publicAccessCreatedAt: enabled ? new Date().toISOString() : null,
                    publicToken
                  });
                }}
              />
            </div>
          </div>
          
          {kiosk.publicAccess === 'enabled' && (
            <div className="mt-6 border-t border-border pt-6 space-y-6">
              <div className="grid gap-6 sm:grid-cols-2">
                {/* Expiration Duration Type */}
                <div>
                  <label className="text-sm font-semibold block mb-2">Duration</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: 'hours', label: 'Hours' },
                      { id: 'days', label: 'Days' },
                      { id: 'permanent', label: 'Permanent' }
                    ].map(opt => (
                      <button
                        type="button"
                        key={opt.id}
                        onClick={() => {
                          const duration = opt.id as any;
                          const expiresAt = calculateExpiration(duration, kiosk.publicAccessDurationValue || 1);
                          update({ 
                            publicAccessDuration: duration,
                            publicAccessExpiresAt: expiresAt
                          });
                        }}
                        className={`rounded-lg py-2 text-xs font-semibold border transition-colors ${kiosk.publicAccessDuration === opt.id ? 'bg-primary/15 text-primary border-primary/20' : 'hover:bg-muted border-border bg-card'}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Expiration Duration Value */}
                {kiosk.publicAccessDuration !== 'permanent' && (
                  <div>
                    <label className="text-sm font-semibold block mb-2">
                      Expire after {kiosk.publicAccessDurationValue || 1} {kiosk.publicAccessDuration}
                    </label>
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        min={1}
                        max={kiosk.publicAccessDuration === 'hours' ? 24 : 30}
                        value={kiosk.publicAccessDurationValue || 1}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          const expiresAt = calculateExpiration(kiosk.publicAccessDuration || 'hours', val);
                          update({ 
                            publicAccessDurationValue: val,
                            publicAccessExpiresAt: expiresAt
                          });
                        }}
                        className="flex-1 accent-primary"
                      />
                      <span className="text-sm font-bold text-primary min-w-[2rem] text-right">
                        {kiosk.publicAccessDurationValue || 1}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Live status and link sharing */}
              <div className="rounded-xl bg-muted/40 p-4 border border-border/60 space-y-4">
                <div className="text-xs">
                  <span className="font-bold text-foreground block mb-1">Status:</span>
                  <span className="text-muted-foreground">
                    {kiosk.publicAccessExpiresAt ? (
                      <>This kiosk is publicly accessible until <strong className="text-primary font-semibold">{new Date(kiosk.publicAccessExpiresAt).toLocaleString()}</strong>.</>
                    ) : (
                      <>This kiosk is <strong className="text-emerald-400 font-semibold">permanently accessible</strong> publicly.</>
                    )}
                  </span>
                </div>

                {kiosk.publicToken && (
                  <div className="space-y-2 pt-2 border-t border-border/60">
                    <span className="text-xs font-bold text-foreground block">Public Shareable Link:</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={`${window.location.origin}/kiosk/${event.id}?token=${kiosk.publicToken}`}
                        className="flex-1 bg-card border border-border rounded-lg px-3 py-1.5 text-xs text-muted-foreground font-mono select-all focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const url = `${window.location.origin}/kiosk/${event.id}?token=${kiosk.publicToken}`;
                          navigator.clipboard.writeText(url);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        }}
                        className={`flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold border transition-all min-w-[5.5rem] ${copied ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-primary text-white border-transparent hover:bg-primary/90'}`}
                      >
                        {copied ? 'Copied!' : 'Copy Link'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const newToken = generateShareToken();
                          update({ publicToken: newToken });
                        }}
                        title="Regenerate Token"
                        className="rounded-lg p-1.5 border border-border bg-card hover:bg-muted text-muted-foreground transition-colors"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      Note: The standard URL <code className="font-mono text-foreground bg-muted px-1 py-0.5 rounded">/kiosk/{event.id}</code> still requires a login. Only guests visiting via this unique link can view it without credentials.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Slide Rotation Interval */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="rounded-lg bg-orange-500/10 p-2 text-orange-400">
              <Clock className="h-5 w-5" />
            </div>
            <span className="font-semibold">{t('kiosk.interval')}</span>
          </div>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min={3}
              max={60}
              value={kiosk.interval}
              onChange={(e) => update({ interval: parseInt(e.target.value) })}
              className="flex-1 accent-primary"
            />
            <span className="min-w-[3rem] text-sm font-bold text-primary">{kiosk.interval}s</span>
          </div>
          <p className="text-xs text-muted-foreground mt-2">{t('kiosk.intervalDesc')}</p>
        </div>

        {/* Transition Effect */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="rounded-lg bg-pink-500/10 p-2 text-pink-400">
              <Sparkles className="h-5 w-5" />
            </div>
            <span className="font-semibold">Transition Effect</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: 'fade', label: 'Fade' },
              { id: 'zoom', label: 'Zoom' },
              { id: 'slide', label: 'Slide' }
            ].map(opt => (
              <button
                type="button"
                key={opt.id}
                onClick={() => update({ transitionEffect: opt.id as any })}
                className={`rounded-lg py-2 text-xs font-semibold border transition-colors ${kiosk.transitionEffect === opt.id ? 'bg-primary/15 text-primary border-primary/20' : 'hover:bg-muted border-border bg-card'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">Animation between photos.</p>
        </div>

        {/* Slideshow Loop */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="rounded-lg bg-indigo-500/10 p-2 text-indigo-400">
                <RefreshCw className="h-5 w-5" />
              </div>
              <div className="flex flex-col">
                <span className="font-semibold">Loop Slideshow</span>
                <span className="text-xs text-muted-foreground mt-0.5">Restart slideshow when reaching the end.</span>
              </div>
            </div>
            <Switch
              checked={kiosk.loopSlideshow !== false}
              onCheckedChange={(loopSlideshow: boolean) => update({ loopSlideshow })}
            />
          </div>
        </div>

        {/* Auto Check for New Photos */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="rounded-lg bg-yellow-500/10 p-2 text-yellow-400">
              <Clock className="h-5 w-5" />
            </div>
            <span className="font-semibold">Auto Refresh Interval</span>
          </div>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min={0}
              max={60}
              value={kiosk.autoRefreshInterval !== undefined ? kiosk.autoRefreshInterval : 10}
              onChange={(e) => update({ autoRefreshInterval: parseInt(e.target.value) })}
              className="flex-1 accent-primary"
            />
            <span className="min-w-[3rem] text-sm font-bold text-primary">
              {kiosk.autoRefreshInterval === 0 ? 'Off' : `${kiosk.autoRefreshInterval || 10}s`}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-2">How frequently the kiosk checks the server for new photo uploads.</p>
        </div>

        {/* Jump to New Photo */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="rounded-lg bg-cyan-500/10 p-2 text-cyan-400">
                <Zap className="h-5 w-5" />
              </div>
              <div className="flex flex-col">
                <span className="font-semibold">Jump to New Photo</span>
                <span className="text-xs text-muted-foreground mt-0.5">Show newly added photos immediately on refresh.</span>
              </div>
            </div>
            <Switch
              checked={kiosk.refreshOnNewPhoto !== false}
              onCheckedChange={(refreshOnNewPhoto: boolean) => update({ refreshOnNewPhoto })}
            />
          </div>
        </div>

        {/* Sorting / Display Order */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="rounded-lg bg-blue-500/10 p-2 text-blue-400">
              <RefreshCw className="h-5 w-5" />
            </div>
            <span className="font-semibold">{t('kiosk.sorting')}</span>
          </div>
          <div className="grid grid-cols-1 gap-2">
            {[
              { id: 'newestFirst', label: t('kiosk.sorting_newest') },
              { id: 'oldestFirst', label: t('kiosk.sorting_oldest') },
              { id: 'random', label: t('kiosk.sorting_random') },
            ].map((opt) => (
              <button
                type="button"
                key={opt.id}
                onClick={() => update({ sortOrder: opt.id as any })}
                className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors ${kiosk.sortOrder === opt.id ? 'bg-primary/15 text-primary border-primary/20' : 'hover:bg-muted border border-transparent'}`}
              >
                {opt.label}
                {kiosk.sortOrder === opt.id && <div className="h-1.5 w-1.5 rounded-full bg-primary" />}
              </button>
            ))}
          </div>
        </div>

        {/* Content Filter */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="rounded-lg bg-purple-500/10 p-2 text-purple-400">
              <Layout className="h-5 w-5" />
            </div>
            <span className="font-semibold">{t('kiosk.filter')}</span>
          </div>
          <div className="grid grid-cols-1 gap-2">
            {[
              { id: 'all', label: t('kiosk.filter_all') },
              { id: 'separate', label: t('kiosk.filter_separate') },
              { id: 'layouts', label: t('kiosk.filter_layouts') },
            ].map((opt) => (
              <button
                type="button"
                key={opt.id}
                onClick={() => update({ contentFilter: opt.id as any })}
                className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors ${kiosk.contentFilter === opt.id ? 'bg-primary/15 text-primary border-primary/20' : 'hover:bg-muted border border-transparent'}`}
              >
                {opt.label}
                {kiosk.contentFilter === opt.id && <div className="h-1.5 w-1.5 rounded-full bg-primary" />}
              </button>
            ))}
          </div>
        </div>

        {/* Overlays */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:col-span-2">
          <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground/40 mb-6">{t('kiosk.displayOptions')}</h3>
          <div className="grid gap-6 sm:grid-cols-2">
             <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                   <div className="rounded-lg bg-zinc-500/10 p-2 text-zinc-400">
                      <Type className="h-5 w-5" />
                   </div>
                   <div className="flex flex-col">
                      <span className="text-sm font-semibold">{t('kiosk.showEventName')}</span>
                      <span className="text-xs text-muted-foreground">{t('kiosk.showEventNameDesc')}</span>
                   </div>
                </div>
                <Switch
                  checked={kiosk.showEventName}
                  onCheckedChange={(val: boolean) => update({ showEventName: val })}
                />
             </div>

             <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                   <div className="rounded-lg bg-zinc-500/10 p-2 text-zinc-400">
                      <QrCode className="h-5 w-5" />
                   </div>
                   <div className="flex flex-col">
                      <span className="text-sm font-semibold">{t('kiosk.showQR')}</span>
                      <span className="text-xs text-muted-foreground">{t('kiosk.showQRDesc')}</span>
                   </div>
                </div>
                <Switch
                  checked={kiosk.showQrCode}
                  onCheckedChange={(val: boolean) => update({ showQrCode: val })}
                />
             </div>
          </div>
        </div>

        {/* Styling Options */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:col-span-2">
          <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground/40 mb-6">Styling Options</h3>
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-zinc-500/10 p-2 text-zinc-400">
                  <Palette className="h-5 w-5" />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-semibold">Ambient Blur Background</span>
                  <span className="text-xs text-muted-foreground">Display enlarged, blurred photo as ambient background backdrop.</span>
                </div>
              </div>
              <Switch
                checked={kiosk.blurBackground !== false}
                onCheckedChange={(val: boolean) => update({ blurBackground: val })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-zinc-500/10 p-2 text-zinc-400">
                  <Palette className="h-5 w-5" />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-semibold">Backdrop Background Color</span>
                  <span className="text-xs text-muted-foreground">Fallback backdrop background color behind photos.</span>
                </div>
              </div>
              <input
                type="color"
                value={kiosk.backgroundColor || '#000000'}
                onChange={(e) => update({ backgroundColor: e.target.value })}
                className="h-8 w-12 rounded border border-border bg-transparent p-0 cursor-pointer"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
